import { zodResolver } from '@hookform/resolvers/zod';
import { AddOutlined, SaveAltOutlined } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useUuid } from '@procureai/ui-mui';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { RuleDetailDialogControls } from '../../hooks/useRuleDetailDialogControls/useRuleDetailDialogControls';
import { useRuleDialogActions } from '../../hooks/useRuleDialogActions/useRuleDialogActions';
import { RuleDetailDialogFormFields } from '../RuleDetailDialogFormFields/RuleDetailDialogFormFields';
import { I18nNamespace } from '~/common/types';
import { DialogActionsLayout } from '~/components/DialogWizard/components/DialogActionsLayout/DialogActionsLayout';
import { RouteId } from '~/routes/RouteId';

import {
  ruleDetailDialogDefaultValues,
  RuleDetailDialogFormData,
  ruleDetailDialogFormSchema,
} from './RuleDetailDialog.schema';

type RuleDetailDialogProps = Pick<RuleDetailDialogControls, 'isOpen' | 'closeDialog'>;

export const RuleDetailDialog = ({ isOpen, closeDialog }: RuleDetailDialogProps) => {
  const { id, variantOptions, create, update, loading, ...initialData } = useRuleDialogActions();
  const { t } = useTranslation(I18nNamespace.ruleBuilder, { keyPrefix: 'RuleDetailDialog' });
  const navigate = useNavigate();

  const submit = async (formData: RuleDetailDialogFormData) => {
    if (id) {
      const { variant, ...data } = formData;
      await update({ id, ...data });
      closeDialog();
    } else {
      const newId = await create(formData);
      navigate(RouteId.ruleDetail(newId), { replace: true });
    }
  };

  const formId = useUuid();
  const form = useForm<RuleDetailDialogFormData>({
    mode: 'onSubmit',
    resolver: zodResolver(ruleDetailDialogFormSchema),
    defaultValues: ruleDetailDialogDefaultValues,
    shouldUnregister: true,
  });

  return (
    <Dialog open={isOpen} onClose={closeDialog} fullWidth maxWidth="sm">
      <DialogTitle>{t('title')}</DialogTitle>
      <FormProvider {...form}>
        <form id={formId} autoComplete="off" noValidate onSubmit={form.handleSubmit(submit)}>
          <DialogContent>
            <RuleDetailDialogFormFields id={id} variantOptions={variantOptions} defaultValues={initialData} />
          </DialogContent>
          <DialogActions>
            <DialogActionsLayout
              left={
                <Button variant="outlined" color="primary" onClick={closeDialog} disabled={loading}>
                  {t('action.cancel')}
                </Button>
              }
              right={
                <Button
                  loading={loading}
                  loadingPosition="end"
                  variant="contained"
                  color="primary"
                  type="submit"
                  endIcon={id ? <SaveAltOutlined /> : <AddOutlined />}
                >
                  {id ? t('action.save') : t('action.create')}
                </Button>
              }
            />
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
